import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Accordion } from "react-bootstrap";
import { fetchAllMarketPlaces, marketPlaceListing } from "../../../store/actions/marketplace.action";
import EbayStep from "./EbayStep";
import "./AddToMarketplaceModal.css";

/**
 * Per-channel registry of which marketplace adapters have a draft-capable
 * UI today. Mirrors MarketplacePushWizard.CHANNEL_TO_STEP but with a separate
 * `implemented` flag — Walmart's wizard step is a stub (no real form yet) so
 * we can't surface draft fields for it from this screen either. Adapter parity
 * lands as Walmart's renderer is built.
 */
const CHANNEL_TO_PANEL = {
    "10000": { name: "eBay",    Component: EbayStep,    marketplaceName: "ebay",    implemented: true  },
    "10003": { name: "Walmart", Component: null,        marketplaceName: "walmart", implemented: false },
    // Amazon, Etsy, TikTok, Shopify — same story; pending each adapter's renderer.
};

/**
 * Marketplace Drafts Section
 *
 * Lets the user pre-fill marketplace-specific listing fields (eBay aspects,
 * Walmart spec, etc.) on the Inventory Product edit page. Each panel writes to
 * Tbl_MarketplaceInventoryListing.DraftPayload via /marketplace/:marketplace/listing/save-draft —
 * no marketplace API is called from here. When the user later opens
 * "Push to Marketplace" from the Inventory grid, the wizard's eBay step
 * pre-fills from the same draft row.
 *
 * Visible on Edit only (`productId` must be present). The parent (Product.js)
 * is responsible for gating that — this component renders nothing if `productId`
 * is falsy.
 */
const MarketplaceDraftsSection = ({ product }) => {
    const dispatch = useDispatch();
    const productId = product?.id || product?.Id;

    // Tbl_Marketplace dropdown — reused from the wizard so name⇄numericId resolution is consistent.
    // Stored in redux as { value: id, label: name } (see fetchAllMarketPlaces reducer).
    const marketPlacesList = useSelector((state) => state.InventoryManagement?.marketPlacesList) || [];

    const [availableChannels, setAvailableChannels] = useState([]);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [loadingChannels, setLoadingChannels] = useState(false);

    useEffect(() => {
        if (!marketPlacesList.length) dispatch(fetchAllMarketPlaces());
    }, [dispatch, marketPlacesList.length]);

    // Load the "marketplace types" catalog (channel_id / channel_name / channel_logo)
    // from the same endpoint AddToMarketplaceModal uses. We filter to the channels
    // we have a draft-capable Component for so the tiles never show a dead end.
    useEffect(() => {
        let cancelled = false;
        setLoadingChannels(true);
        Promise.resolve(marketPlaceListing(0))
            .then((res) => {
                if (cancelled) return;
                const all = res?.data?.EntityList || [];
                setAvailableChannels(all);
            })
            .catch(() => { if (!cancelled) setAvailableChannels([]); })
            .finally(() => { if (!cancelled) setLoadingChannels(false); });
        return () => { cancelled = true; };
    }, []);

    // Channels we have a real draft-capable Component for. Implementation-pending
    // channels still render as a tile so the user understands what's coming, but
    // they can't be toggled (button disabled).
    const supportedChannels = useMemo(() => {
        return (availableChannels || []).filter((c) => CHANNEL_TO_PANEL[String(c.channel_id)]);
    }, [availableChannels]);

    const toggleChannel = (channelId) => {
        const cfg = CHANNEL_TO_PANEL[String(channelId)];
        if (!cfg?.implemented) return;
        setSelectedChannels((prev) => (
            prev.includes(channelId)
                ? prev.filter((id) => id !== channelId)
                : [...prev, channelId]
        ));
    };

    // Resolve numeric Tbl_Marketplace.Id by name — same trick MarketplacePushWizard uses.
    // The save-draft request needs this on the wire (the .NET SP keys on it).
    const marketplaceIdFor = (marketplaceName) => {
        const match = marketPlacesList.find(
            (m) => String(m.label || "").toLowerCase() === marketplaceName.toLowerCase(),
        );
        return match?.value || null;
    };

    if (!productId) {
        // Add mode hasn't saved the product yet. Parent (Product.js) controls when
        // we mount; rendering a placeholder here would be redundant.
        return null;
    }

    return (
        <Accordion className="mt-3" defaultActiveKey="marketplace-drafts">
            <Accordion.Item eventKey="marketplace-drafts">
                <Accordion.Header>
                    <span className="fnt-wgt-editmgmt">Marketplace Listing Fields (Optional)</span>
                </Accordion.Header>
                <Accordion.Body>
                    <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                        Pre-fill marketplace-specific fields for this product. Saved fields are
                        stored as drafts and will appear pre-populated when you open
                        &quot;Push to Marketplace&quot; from the Inventory list. No marketplace API
                        is called from this page.
                    </p>

                    {loadingChannels && (
                        <p className="text-muted" style={{ fontSize: 13 }}>Loading marketplaces…</p>
                    )}

                    {!loadingChannels && supportedChannels.length === 0 && (
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            No marketplaces configured for this seller.
                        </p>
                    )}

                    {!loadingChannels && supportedChannels.length > 0 && (
                        <div className="integrationListWrapper row">
                            {supportedChannels.map((channel) => {
                                const cfg = CHANNEL_TO_PANEL[String(channel.channel_id)];
                                const isSelected = selectedChannels.includes(channel.channel_id);
                                const disabled = !cfg.implemented;
                                return (
                                    <button
                                        key={channel.channel_id}
                                        type="button"
                                        className="col-lg-3 col-sm-12 sortBtn"
                                        onClick={() => toggleChannel(channel.channel_id)}
                                        disabled={disabled}
                                        style={{ border: "none", padding: 0, background: "none", opacity: disabled ? 0.5 : 1 }}
                                    >
                                        <div className={`integrationCard ${isSelected ? "selected" : ""}`}>
                                            {isSelected && (
                                                <div className="selected-checkmark">
                                                    <i className="fa fa-check" aria-hidden="true" />
                                                </div>
                                            )}
                                            <div className="image">
                                                <img
                                                    src={channel.channel_logo}
                                                    alt={channel.channel_name}
                                                    style={{ maxWidth: "100%", maxHeight: 100 }}
                                                />
                                            </div>
                                            <span className="btn cus-primary-bg-btn custom-btn-shipment ingration">
                                                {channel.channel_name}
                                                {!cfg.implemented && (
                                                    <span style={{ fontSize: 10, display: "block", marginTop: 2 }}>
                                                        (coming soon)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Render the selected marketplace panels stacked. Each is fully self-contained
                        (loads its own prereqs, holds its own form state, saves its own draft) so
                        the section is just a collection of mounted EbayStep-like components. */}
                    {selectedChannels.length > 0 && (
                        <div className="mt-4">
                            {selectedChannels.map((channelId) => {
                                const cfg = CHANNEL_TO_PANEL[String(channelId)];
                                if (!cfg?.Component) return null;
                                const Panel = cfg.Component;
                                const marketplaceId = marketplaceIdFor(cfg.marketplaceName);
                                return (
                                    <div key={channelId} className="mb-4 p-3" style={{ border: "1px solid #e0e0e0", borderRadius: 6 }}>
                                        <h6 className="mb-3">{cfg.name} Listing Fields</h6>
                                        <Panel
                                            product={product}
                                            marketplaceId={marketplaceId}
                                            draftOnly
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

MarketplaceDraftsSection.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        companySellerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productSku: PropTypes.string,
        productName: PropTypes.string,
    }),
};
MarketplaceDraftsSection.defaultProps = { product: null };

export default MarketplaceDraftsSection;
